// src/App.tsx
import React, { useEffect, useState } from 'react';
import { Gauge, Boxes, Radar, Route, Gem, Scale, Siren, SlidersHorizontal } from 'lucide-react';
import {useSimulationStore} from './store/useSimulationStore';
import {useAuthStore} from './store/useAuthStore';
import {useWebSocket} from './hooks/useWebSocket';
import DashboardView from './components/views/DashboardView';
import NodesView from './components/views/NodesView';
import SpatialNodesView from './components/views/SpatialNodesView';
import ConnectionsView from './components/views/ConnectionsView';
import PoliciesView from './components/views/PoliciesView';
import NodeDetailModal from './components/modals/NodeDetailModal';
import ConnectionDetailModal from './components/modals/ConnectionDetailModal';
import HeartbeatIndicator from './components/common/HeartbeatIndicator';
import ResourcesView from './components/views/ResourcesView';
import ResourceNodeDetailModal from './components/modals/ResourceNodeDetailModal';
import SettingsView from './components/views/SettingsView';
import AlertsView from './components/views/AlertsView';
import LoginView from './components/views/LoginView';

type ViewId = 'dashboard' | 'nodes' | 'spatial' | 'connections' | 'policies' | 'resources' | 'alerts' | 'settings';

interface ViewMeta {
   id: ViewId;
   hudId: string;       // id hud.css's #hud[data-view="…"] accent selectors key on
   label: string;
   Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
   crumb: string;
   name: string;
   title: string;
   sub: string;
   mode: string;
   ch: string;
}

const VIEWS: ViewMeta[] = [
   { id: 'dashboard',   hudId: 'dashboard',   label: 'Dashboard',   Icon: Gauge,             crumb: 'SYSTEM //',   name: 'COMMAND',    title: 'Command Overview',           sub: 'Network Telemetry',         mode: 'OVW',  ch: '01' },
   { id: 'nodes',       hudId: 'nodes',       label: 'Nodes',       Icon: Boxes,             crumb: 'REGISTRY //', name: 'ENTITIES',   title: 'Tracked Entities · Nodes',   sub: 'Population & Stability',    mode: 'NODE', ch: '02' },
   { id: 'spatial',     hudId: 'map',         label: 'Map',         Icon: Radar,             crumb: 'SECTOR //',   name: 'PLAN VIEW',  title: 'Plan View · Spatial Grid',   sub: 'Tracked Entities & Routes', mode: 'PLAN', ch: '03' },
   { id: 'connections', hudId: 'connections', label: 'Connections', Icon: Route,             crumb: 'NETWORK //',  name: 'ROUTES',     title: 'Routes · Connections',       sub: 'Traffic & Trade Links',     mode: 'LINK', ch: '04' },
   { id: 'resources',   hudId: 'resources',   label: 'Resources',   Icon: Gem,               crumb: 'FIELD //',    name: 'DEPOSITS',   title: 'Resource Deposits',          sub: 'Yield & Reserves',          mode: 'RES',  ch: '05' },
   { id: 'policies',    hudId: 'policies',    label: 'Policies',    Icon: Scale,             crumb: 'CONTROL //',  name: 'DIRECTIVES', title: 'Directives · Policies',      sub: 'Enacted Governance',        mode: 'POL',  ch: '06' },
   { id: 'alerts',      hudId: 'alerts',      label: 'Alerts',      Icon: Siren,             crumb: 'FEED //',     name: 'SIGNALS',    title: 'Signal Feed · Alerts',       sub: 'Threat & Event Log',        mode: 'ALRT', ch: '07' },
   { id: 'settings',    hudId: 'settings',    label: 'Settings',    Icon: SlidersHorizontal, crumb: 'ADMIN //',    name: 'CONFIG',     title: 'System Config',              sub: 'Simulation Parameters',     mode: 'CFG',  ch: '08' },
];

function App() {
   const isConnected = useSimulationStore(s => s.isConnected);
   const isReconnecting = useSimulationStore(s => s.isReconnecting);
   const activeView = useSimulationStore(s => s.activeView);
   const setActiveView = useSimulationStore(s => s.setActiveView);
   const nodes = useSimulationStore(s => s.nodes);
   const connections = useSimulationStore(s => s.connections);
   const alerts = useSimulationStore(s => s.alerts);

   const { isAuthenticated, username, logout, checkAuth } = useAuthStore();

   const [scan, setScan] = useState(0);

   useEffect(() => { checkAuth(); }, [checkAuth]);

   useWebSocket();

   // Decorative scan-line readout tick — cosmetic only, no backing data.
   useEffect(() => {
      const interval = setInterval(() => setScan(s => (s + 1) % 10000), 900);
      return () => clearInterval(interval);
   }, []);

   // Keyboard shortcuts: 1–8 switch views.
   useEffect(() => {
      const handler = (e: KeyboardEvent) => {
         if (!isAuthenticated) return;
         const target = e.target as HTMLElement | null;
         if (target && /INPUT|TEXTAREA/.test(target.tagName)) return;
         const n = parseInt(e.key, 10);
         if (n >= 1 && n <= VIEWS.length) setActiveView(VIEWS[n - 1].id);
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
   }, [isAuthenticated, setActiveView]);

   if (!isAuthenticated) {
      return (
         <div id="login">
            <div className="login-bar">
               <div className="sig-mark" />
               <b>SYS//SIM</b>
               <span className="req">Authentication Required</span>
            </div>
            <div className="login-stage">
               <div className="login-frame">
                  <span className="ret tl" /><span className="ret tr" />
                  <span className="ret bl" /><span className="ret br" />
                  <div className="hud-skin">
                     <LoginView />
                  </div>
               </div>
            </div>
            <div className="login-foot">
               <span>TERMINAL <span className="val">SYS-01</span></span>
               <span>CIPHER <span className="val">AES-256</span></span>
               <span>LINK <span className="val">SECURE</span></span>
            </div>
         </div>
      );
   }

   const active = VIEWS.find(v => v.id === activeView) ?? VIEWS[0];
   const activeTrades = connections.filter(c => (Number(c.last_trade_volume) || 0) > 0).length;
   const criticalAlerts = alerts.filter(a => a.severity === 'critical').length;

   const wsColor = isReconnecting ? '#facc15' : isConnected ? '#42d67e' : '#f87171';
   const wsLabel = isReconnecting ? 'Reconnecting' : isConnected ? 'Online' : 'Offline';

   const renderView = () => {
      switch (activeView) {
         case 'dashboard':
            return <DashboardView />;
         case 'nodes':
            return <NodesView />;
         case 'spatial':
            return <SpatialNodesView />;
         case 'connections':
            return <ConnectionsView />;
         case 'policies':
            return <PoliciesView />;
         case 'resources':
            return <ResourcesView />;
         case 'alerts':
            return <AlertsView />;
         case 'settings':
            return <SettingsView />;
         default:
            return <DashboardView />;
      }
   };

   return (
      <>
         <div id="hud" data-view={active.hudId}>

            {/* ── Rail ── */}
            <aside className="rail">
               <div className="rail__sig">
                  <div className="sig-mark" />
                  <div>
                     <b>SYS//SIM</b>
                     <span>Urban Network Simulation</span>
                  </div>
               </div>

               <div className="rail__hd">Command Channels</div>
               <nav className="nav">
                  {VIEWS.map((v, i) => (
                     <button
                        key={v.id}
                        type="button"
                        aria-current={activeView === v.id}
                        onClick={() => setActiveView(v.id)}
                     >
                        <span className="ix">{String(i + 1).padStart(2, '0')}</span>
                        <span className="ic"><v.Icon className="w-[15px] h-[15px]" strokeWidth={1.7} /></span>
                        <span className="lbl">{v.label}</span>
                        <span className="dot" />
                     </button>
                  ))}
               </nav>

               <div className="rail__foot">
                  <div className="linkrow">
                     <span>WS Link</span>
                     <b style={{ color: wsColor }}>{wsLabel}</b>
                  </div>
                  <div className="linkrow">
                     <span>Entities</span>
                     <span>{nodes.length} nodes · {connections.length} links</span>
                  </div>
                  <div className="linkrow">
                     <span>Operator</span>
                     <span>{username}</span>
                  </div>
                  <button className="lockbtn" onClick={logout}>
                     <span className="k" />Lock Terminal
                  </button>
               </div>
            </aside>

            {/* ── Topbar ── */}
            <header className="topbar">
               <div className="tb-title">
                  <span className="glyph">◈</span>
                  <span className="crumb">{active.crumb}</span>
                  <span className="name">{active.name}</span>
               </div>
               <div className="tb-readout">
                  <span>LAT <span className="val">+084.20</span></span>
                  <span>LON <span className="val">−160.00</span></span>
                  <span>SCL <span className="val">1.00×</span></span>
                  {criticalAlerts > 0 && (
                     <span className="text-red-400">CRIT <span className="val">{criticalAlerts}</span></span>
                  )}
                  {activeTrades > 0 && (
                     <span className="text-purple-400">TRADE <span className="val">{activeTrades}</span></span>
                  )}
                  <span className="tb-sep" />
                  <span>SCAN <span className="val">{String(scan).padStart(4, '0')}</span></span>
                  <span className="tb-sep" />
                  <div id="hud-beat"><HeartbeatIndicator /></div>
               </div>
            </header>

            {/* ── Stage ── */}
            <main className="stage">
               <div className="stage-frame">
                  <span className="ret tl" /><span className="ret tr" />
                  <span className="ret bl" /><span className="ret br" />

                  <div className="stage-head">
                     <span className="tick">▤</span>
                     <h1>{active.title}</h1>
                     <span className="sub">{active.sub}</span>
                     <div className="meta">
                        <span>MODE <b>{active.mode}</b></span>
                        <span>CH <b>{active.ch}</b></span>
                        <span>STATUS <b>ACTIVE</b></span>
                     </div>
                  </div>

                  <div className="stage-body">
                     <div className="hud-skin">
                        {renderView()}
                     </div>
                  </div>
               </div>
            </main>
         </div>

         {/* ── Modals ── */}
         <div id="hud-modals" data-view={active.hudId} className="hud-skin">
            <NodeDetailModal />
            <ConnectionDetailModal />
            <ResourceNodeDetailModal />
         </div>
      </>
   );
}

export default App;
